import React from "react";
import { ActionIcon, CopyButton, Flex, Tooltip } from "@mantine/core";

import { IconCheck, IconCopy } from "@tabler/icons-react";
import { formatHexStr } from "../formatHexStr";
import {
  formatTimeStamp,
  LONG_DISPLAY_FORMAT,
} from "../collectionFound/dateTimeUtils";
import LogoVBSvg from "../../svg/LogoVBSvg";
import LogoBlockchainSvg from "../../svg/LogoBlockchainSvg";
import DownloadPDFUserFound from "../../components/DownloadPDFUserFound";
import { parseDate } from "../collectionFound/resultScreen";
import { buildVerifyStampPath } from "../verifyStampLink";

// Function for creating data
export const collectionTableData = (
  filteredItems,
  staticBasePath,
  userTimezone,
  theme,
  classesIcons,
) => {
  if (!Array.isArray(filteredItems)) {
    return [];
  }

  return filteredItems.map((item) => ({
    objectCid: (
      <Flex align={theme.flex.align.center} gap={theme.flex.gap.tiny}>
        {item?.objectCid ? formatHexStr(item?.objectCid, 6, 4, 13) : "-"}
        <CopyButton value={item.objectCid} timeout={2000}>
          {({ copied, copy }) => (
            <Tooltip
              label={copied ? "Copied" : "Copy"}
              className={classesIcons.copy}
              withArrow
              position="right"
            >
              <ActionIcon
                color={
                  copied
                    ? theme.customColors.actionIconFirst
                    : theme.customColors.actionIconSecond
                }
                variant="subtle"
                onClick={copy}
              >
                {copied ? (
                  <IconCheck className={classesIcons.width} />
                ) : (
                  <IconCopy className={classesIcons.width} />
                )}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Flex>
    ),
    timestamp: formatTimeStamp(
      item?.timestamp,
      userTimezone,
      LONG_DISPLAY_FORMAT,
    ),
    blockchain_receipts: (
      <Flex
        align={theme.flex.align.center}
        justify={theme.flex.justify.center}
        gap={theme.flex.gap.medium}
      >
        {item?.objectCid && (
          <a
            title="vBase Stamp Verification"
            href={buildVerifyStampPath({
              objectCid: item?.objectCid,
              transactionHash: item?.transactionHash,
              collectionCid: item?.collection_hash,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LogoVBSvg />
          </a>
        )}
        {item?.transactionHash && (
          <a
            title="Public Blockchain Record"
            href={`${item.blockExplorerUrl}${item?.transactionHash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LogoBlockchainSvg />
          </a>
        )}
        <DownloadPDFUserFound
          user_id={item.user_id}
          user={item.user}
          collection_name={item.collection_name}
          collection_hash={item.collection_hash}
          objectCid={item.objectCid}
          blockExplorerUrl={item.blockExplorerUrl}
          transactionHash={item.transactionHash}
          chainId={item.chainId}
          blockchainName={item.blockchainName}
          formattedDateTime={
            formatTimeStamp(item.timestamp, userTimezone, LONG_DISPLAY_FORMAT) +
            " " +
            userTimezone
          }
        />
      </Flex>
    ),
  }));
};

// Function for creating columns
export const collectionTableColumns = (theme, userTimezone) => [
  {
    accessorKey: "objectCid",
    header: "Content ID",
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: "timestamp",
    header: `Timestamp (${userTimezone || "UTC"})`,
    size: 230,
    sortingFn: (rowA, rowB) => {
      const timestampA = parseDate(rowA.original.timestamp);
      const timestampB = parseDate(rowB.original.timestamp);
      return timestampA - timestampB;
    },
  },
  {
    accessorKey: "blockchain_receipts",
    header: <Flex>Receipt Links</Flex>,
    enableSorting: false,
    size: 140,
    mantineTableHeadCellProps: {
      style: {
        display: "block",
        marginLeft: "auto",
        marginRight: "auto",
        fontSize: "1.142em",
        fontFamily: theme.headings.fontFamily.bold,
      },
    },
    mantineTableBodyCellProps: {
      align: "center",
    },
  },
];

// Function for configuring the table
export const collectionTableOptions = (columns, data, faIcons, theme) => ({
  columns,
  data,
  icons: faIcons,
  paginationDisplayMode: "pages",
  enableFullScreenToggle: false,
  enableTopToolbar: false,
  enableBottomToolbar: true,
  enableColumnActions: false,
  initialState: {
    pagination: { pageIndex: 0, pageSize: 20 },
    sorting: [{ id: "timestamp", desc: true }],
  },
  withColumnBorders: true,
  mantinePaginationProps: () => ({
    showRowsPerPage: false,
    color: theme.customBackgrounds.table,
    autoContrast: true,
    style: {
      paddingBottom: "1.75em",
    },
  }),
  mantineTableProps: {
    style: {
      border: "none",
      borderCollapse: "collapse",
    },
    className: "html-table-mantine",
  },
  mantinePaperProps: {
    withBorder: false,
    shadow: "none",
  },
  mantineTableBodyCellProps: {
    style: {
      padding: "0.6875em 0.9375em 0.5625em 0.9375em",
      fontSize: "1.142em",
    },
  },
  mantineTableHeadCellProps: {
    style: {
      padding: "0.55em 0.9375em 0.4375em 0.9375em",
      fontSize: "1.142em",
      fontFamily: theme.headings.fontFamily.bold,
    },
  },
  mantineTableBodyRowProps: () => ({
    style: {
      borderBottom: theme.customColors.tableBorder,
    },
  }),
  mantineTableHeadRowProps: () => ({
    style: {
      backgroundColor: theme.customBackgrounds.table,
    },
  }),
});
